<template>
    <v-dialog
        :value="display"
        max-width="640"
        persistent
    >
        <LoadingSendingMail :value="loadingMail" />
        <MessageOverlay ref="overlay" @endSuccess="endOverlaySuccess" />
        
        <v-card v-if="displayVerificationCode">
            <v-card-title>
                <span class="text-h5">Please enter the code you received by email.</span>
            </v-card-title>
            <v-card-text class="px-2 px-sm-6">
                <div class="text-right text-subtitle-2 font-weight-regular mb-1">* {{ $vuetify.lang.t('$vuetify.signIn.required') }}</div>
                <v-form
                    ref="verifCodeForm"
                    v-model="verifCodeFormValidation"
                    lazy-validation
                >
                    <v-text-field 
                        v-model="verificationCode"
                        outlined
                        required
                        :label="$vuetify.lang.t('$vuetify.signIn.verificationCode')+'*'"
                    ></v-text-field>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    :disabled="!verifCodeFormValidation"
                    :class="{ 'gradient-wintech': (verifCodeFormValidation) }"
                    @click="validateVerifCode"
                >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ $vuetify.lang.t('$vuetify.signIn.validate') }}
                </v-btn>
                <v-btn
                    color="error"
                    @click="closeSignInDialog"
                >
                    <v-icon left>mdi-cancel</v-icon>
                    {{ $vuetify.lang.t('$vuetify.signIn.cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-else>
            <v-card-title>
                <span class="text-h5">Please fill in this form in order to create an account on UTC-SUPERVISION.</span>
            </v-card-title>
            <v-card-text class="px-2 px-sm-6">
                <div class="text-right text-subtitle-2 font-weight-regular mb-1">* {{ $vuetify.lang.t('$vuetify.signIn.required') }}</div>
                <v-form
                    ref="signInForm"
                    v-model="signInFormValidation"
                    lazy-validation
                >
                    <v-text-field
                        v-model="signInItem.email"
                        required
                        outlined
                        :rules="emailRules"
                        :label="$vuetify.lang.t('$vuetify.signIn.email') + '*'"
                        @input="isNewEmail = true"
                    ></v-text-field>
                     <v-text-field
                        v-model="signInItem.username"
                        required
                        outlined
                        clearable
                        :rules="nameRules"
                        :label="$vuetify.lang.t('$vuetify.signIn.username') + '*'"
                    ></v-text-field>
                    
                    <vue-tel-input-vuetify
                        outlined
                        clearable
                        placeholder=""
                        appendIcon="$dropdown"
                        mode="international"
                        type="tel"
                        :value="signInItem.phone"
                        :label="phoneInputLabel"
                        :select-label="countryInputLabel"
                        :valid-characters-only="true"
                        :error-messages="!isPhoneValid ? $vuetify.lang.t('$vuetify.signIn.phonenumbermustbevalid') : ''"
                        @input="onTelephoneInput"
                    ></vue-tel-input-vuetify>
                    <v-text-field
                        v-model="newPassword.value"
                        outlined
                        required
                        :rules="newPassword.rules"
                        :label="$vuetify.lang.t('$vuetify.signIn.newpassword')+'*'"
                        :type="newPassword.show ? 'text' : 'password'"
                        :append-icon="newPassword.focus ? newPassword.show ? 'mdi-eye-off' : 'mdi-eye' : undefined"
                        @click:append="newPassword.show = !newPassword.show"
                        @focus="newPassword.focus = true"
                        @blur="newPassword.focus = false"
                    ></v-text-field>
                    <v-text-field
                        v-model="confirmationPassword.value"
                        outlined
                        required
                        :rules="confirmationPassword.rules"
                        :label="$vuetify.lang.t('$vuetify.signIn.passwordconfirmation')+'*'"
                        :type="confirmationPassword.show ? 'text' : 'password'"
                        :append-icon="confirmationPassword.focus ? confirmationPassword.show ? 'mdi-eye-off' : 'mdi-eye' : undefined"
                        @click:append="confirmationPassword.show = !confirmationPassword.show"
                        @focus="confirmationPassword.focus = true"
                        @blur="confirmationPassword.focus = false"
                    ></v-text-field>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    :disabled="!signInFormValidation || !isPhoneValid"
                    :class="{ 'gradient-wintech': (signInFormValidation && isPhoneValid) }"
                    @click="validateSignIn"
                >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ $vuetify.lang.t('$vuetify.signIn.validate') }}
                </v-btn>
                <v-btn
                    color="error"
                    @click="closeSignInDialog"
                >
                    <v-icon left>mdi-cancel</v-icon>
                    {{ $vuetify.lang.t('$vuetify.signIn.cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
    import axios from 'axios';
    import { sha3_512 } from 'js-sha3';
    import LoadingSendingMail from '@/components/Overlay/LoadingSendingMail.vue';
    import MessageOverlay from './../Overlay/MessageOverlay.vue';
    export default {
        name: "Invite",
        components: {MessageOverlay, LoadingSendingMail},
        props: {
            display: Boolean,
        },
        data() {
            return {
                signInFormValidation: false,
                verifCodeFormValidation: false,
                displayVerificationCode: false,
                randomNb: '',
                verificationCode: '',
                loadingMail: false,
                alreadyInvitedUser: false,
                nameRules: [
                    v => !!v || this.$vuetify.lang.t('$vuetify.signIn.nameisrequired'),
                ],
                signInItem: {
                    username: '',
                    email: '',
                    phone: '',
                    password: '',
                },

                newPassword: {
                    value: "",
                    show: false,
                    focus: false,
                    rules: [
                        v => !!v ||  this.$vuetify.lang.t('$vuetify.signIn.passwordisrequired'),
                        v => /^(?=.*[A-Z])/.test(v) || this.$vuetify.lang.t('$vuetify.signIn.yourpasswordshouldcontainatleastoneuppercase'),
                        v => /^(?=.*\d)/.test(v) || this.$vuetify.lang.t('$vuetify.signIn.yourpasswordshouldcontainatleastonedigit'),
                        v => /^(?=.*[a-z])/.test(v) || this.$vuetify.lang.t('$vuetify.signIn.yourpasswordshouldcontainatleastonelowercase'),
                        v => /^(?=.*[!@#$%^&*])/.test(v) || this.$vuetify.lang.t('$vuetify.signIn.yourpasswordmustcontainatleastonespecialcharacter'),
                        v => /^[a-zA-Z0-9!@#$%^&*]{8,}/.test(v) || this.$vuetify.lang.t('$vuetify.signIn.yourpasswordshouldcontainatleast8characters'),
                    ]
                },
                confirmationPassword: {
                    value: "",
                    show: false,
                    focus: false,
                    rules: [
                        v => !!v ||  this.$vuetify.lang.t('$vuetify.signIn.confirmationisrequired'),
                    ]
                },
                emailRules: [
                    v => !!v || 'Email is required',
                    v => /.+@.+\..+/.test(v) || 'Email must be valid',
                ],
                passwordRules: [
                    v => !!v || 'Password is required'
                ],
            }
        },
        computed: {
            isPhoneValid() {
                return this.signInItem.phone === '' || this.isPhoneInputValid
            },
            countryInputLabel() {
                return this.$vuetify.lang.t('$vuetify.signIn.country') ;
            },
            phoneInputLabel() {
                return this.$vuetify.lang.t('$vuetify.signIn.phonenumber') ;
            },
        },
        methods: {
            onTelephoneInput(formattedNumber, { number, valid }) {
                this.signInItem.phone = "e164" in number ? number.e164 : number.input;
                this.isPhoneInputValid = valid;
            },
            async validateSignIn() {
                try {
                    if (this.$refs.signInForm.validate() ) {
                        if(this.confirmationPassword.value != this.newPassword.value) {
                            this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.signIn.confirmationPasswordNotTheSame'))
                            return;
                        }
                        this.loadingMail=true;
                        const res = await axios.post('signIn', {email: this.signInItem.email});
                        if (res.status == 200) { //exist
                            this.randomNb = res.data;
                            this.displayVerificationCode = true;
                        } else if(res.status == 204) { //new
                            this.randomNb = res.data;
                            this.displayVerificationCode = true;
                            this.alreadyInvitedUser=true;
                        } else if(res.status == 208) {
                            this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.signIn.emailalreadyinuse'))
                        }
                        this.loadingMail=false;
                    }
                } catch(error) {
                    if ('response' in error && error.response.status === 409) this.isNewEmail = false;
                    else {
                        this.closeSignInDialog();
                        this.showOverlay("error", error);
                    }
                }
            },
            async validateVerifCode() {
                try {
                    if (this.$refs.verifCodeForm.validate() ) {
                        if(this.randomNb != this.verificationCode) {
                            this.$refs.overlay.showOverlay("error", this.$vuetify.lang.t('$vuetify.signIn.wrongverificationcode'))
                            return;
                        }
                        this.signInItem.password = sha3_512(this.newPassword.value)
                        const data = {
                            ...this.signInItem,
                            boolToInsert: this.alreadyInvitedUser
                        }
                        const res = await axios.post('signInValid', data);
                        if (res.status == 200) {
                            this.$refs.overlay.showOverlay("success", this.$vuetify.lang.t('$vuetify.signIn.inscriptionValidate'))
                        }
                    }
                } catch(error) {
                    if ('response' in error && error.response.status === 409) this.isNewEmail = false;
                    else {
                        this.closeSignInDialog();
                        this.showOverlay("error", error);
                    }
                }
            },

            closeSignInDialog() {
                this.signInItem= {
                    username: '',
                    email: '',
                    phone: '',
                    password: '',
                },
                this.$emit("cancel");
            },
            endOverlaySuccess() {
                this.closeSignInDialog();
                this.$router.push("/login");
            },
        }
    }
</script>